import React, { ChangeEvent, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Tabs,
  Tab,
  Box,
  TextField,
  CardMedia,
  Input,
  CircularProgress,
} from "@mui/material";
import EditText from "../../components/editText";
import AddFaq from "../../Modals/addFaq";
import AddIcon from "@mui/icons-material/Add";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import CloseIcon from "@mui/icons-material/Close";
import SwitchToggle from "../../components/SwitchToggle";
import { useGetFaqs, useDeleteFaq } from "../../api/hooks/useFaq";
import { useGetCms, useUpdateCms } from "../../api/hooks/useCms";
import { showSuccess, showError } from "../../helpers/toast";
import type { Faq } from "../../api/types/faq.types";
import type { UpdateCmsPayload } from "../../api/types/cms.types";
import ConfirmationDialog from "../../components/ConfirmationDialog";
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ManageCms = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const [legal2, setLegal2] = useState<string>("");
  const [legal3, setLegal3] = useState<string>("");
  const [legal4, setLegal4] = useState<string>("");

  const [valueTabs, setValueTabs] = React.useState(0);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const [openModal, setOpenModal] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState<Faq | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState<string | null>(null);
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedFaq(null);
  };
  const [error, setError] = useState(false);
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  // Fetch CMS data
  const { data: cmsData, isLoading: isLoadingCms } = useGetCms();

  // Update CMS mutation
  const { mutate: updateCms, isPending: isUpdatingCms } = useUpdateCms({
    onSuccess: (response) => {
      showSuccess(response.message || "CMS updated successfully");
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to update CMS");
    },
  });

  // Debounce search term (500ms delay)
  React.useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  // Fetch FAQs
  const { data: faqsData, isLoading: isLoadingFaqs, refetch: refetchFaqs } = useGetFaqs(
    {
      ...(debouncedSearchTerm.trim() && { search: debouncedSearchTerm.trim() }),
    },
    { enabled: true }
  );

  // Populate form fields when CMS data is loaded
  React.useEffect(() => {
    if (cmsData?.data) {
      const data = cmsData.data;
      if (data.email) setEmail(data.email);
      if (data.phone) setPhone(data.phone);
      if (data.countryCode) {
        setCountryCode(data.countryCode);
      }
      if (data.privacyPolicy) setLegal2(data.privacyPolicy);
      if (data.termsAndConditions) setLegal3(data.termsAndConditions);
      if (data.aboutUs) setLegal4(data.aboutUs);
    }
  }, [cmsData]);

  // Handle save Contact Support (type: 1)
  const handleSaveContactSupport = () => {
    setError(true);
    setPhoneError("");
    
    if (!email.trim()) {
      showError("Email is required");
      return;
    }
    if (!phone.trim()) {
      showError("Phone number is required");
      setPhoneError("Phone number is required");
      return;
    }
    
    // Validate phone number length (8-13 digits)
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length < 8) {
      showError("Phone number must be at least 8 digits");
      setPhoneError("Phone number must be at least 8 digits");
      return;
    }
    if (phoneDigits.length > 13) {
      showError("Phone number cannot exceed 13 digits");
      setPhoneError("Phone number cannot exceed 13 digits");
      return;
    }
    
    setError(false);
    setPhoneError("");

    const payload: UpdateCmsPayload = {
      type: 1,
      email: email.trim(),
      phone: phone.trim(),
      countryCode: countryCode,
    };
    updateCms(payload);
  };

  // Handle save Privacy Policy (type: 1)
  const handleSavePrivacyPolicy = () => {
    if (!legal2.trim()) {
      showError("Privacy Policy content is required");
      return;
    }
    const payload: UpdateCmsPayload = {
      type: 1,
      privacyPolicy: legal2.trim(),
    };
    updateCms(payload);
  };

  // Handle save Terms & Conditions (type: 1)
  const handleSaveTermsConditions = () => {
    if (!legal3.trim()) {
      showError("Terms & Conditions content is required");
      return;
    }
    const payload: UpdateCmsPayload = {
      type: 1,
      termsAndConditions: legal3.trim(),
    };
    updateCms(payload);
  };

  // Handle save About Us (type: 1)
  const handleSaveAboutUs = () => {
    if (!legal4.trim()) {
      showError("About Us content is required");
      return;
    }
    const payload: UpdateCmsPayload = {
      type: 1,
      aboutUs: legal4.trim(),
    };
    updateCms(payload);
  };

  // Delete FAQ mutation
  const { mutate: deleteFaq, isPending: isDeleting } = useDeleteFaq({
    onSuccess: (response) => {
      showSuccess(response.message || "FAQ deleted successfully");
      refetchFaqs();
      setDeleteDialogOpen(false);
      setFaqToDelete(null);
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to delete FAQ");
    },
  });

  // Get FAQs list - ensure it's always an array
  const faqs = useMemo(() => {
    const list = faqsData?.data?.list;
    if (Array.isArray(list)) {
      return list;
    }
    return [];
  }, [faqsData]);

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  // Handle edit FAQ
  const handleEditFaq = (faq: Faq) => {
    setSelectedFaq(faq);
    setOpenModal(true);
  };

  // Handle delete FAQ
  const handleDeleteFaq = (id: string) => {
    setFaqToDelete(id);
    setDeleteDialogOpen(true);
  };

  // Confirm delete FAQ
  const handleConfirmDelete = () => {
    if (faqToDelete) {
      deleteFaq(faqToDelete);
    }
  };

  // Cancel delete FAQ
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setFaqToDelete(null);
  };

  // Handle add new FAQ
  const handleAddFaq = () => {
    setSelectedFaq(null);
    setOpenModal(true);
  };
  return (
    <>
      <div className="main_title">
        <h1>Manage CMS</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Manage CMS
        </p>
      </div>

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="Contact Support" {...a11yProps(0)} />
              <Tab label="Privacy Policy" {...a11yProps(1)} />
              <Tab label="Terms & Conditions" {...a11yProps(2)} />
              <Tab label="About" {...a11yProps(3)} />
              <Tab label="FAQ's" {...a11yProps(4)} />
             
            </Tabs>
          </Box>
        </div>
      </div>

      <div className="cards">
        <CustomTabPanel value={valueTabs} index={0}>
          <form className="form">
            <div className="gap_p">
              <div className="control_group w_50">
                <label htmlFor="email">Email Address</label>
                <TextField
                  id="email"
                  placeholder="Enter here"
                  fullWidth
                  hiddenLabel
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {error && email == "" ? (
                  <h6 className="err_msg">Email is required</h6>
                ) : (
                  ""
                )}
              </div>
              <div className="control_group w_50">
                <label htmlFor="phone">Phone Number</label>
                <PhoneInput
                  country={"us"}
                  placeholder="Enter Phone Number"
                  enableSearch={false}
                  autoFormat={false}
                  inputStyle={{ width: "100%" }}
                  value={phone ? (countryCode ? `${countryCode.replace('+', '')}${phone}` : phone) : ''}
                  onChange={(value: string, data: any) => {
                    let phoneNumber = "";
                    if (data?.dialCode) {
                      const dialCode = data.dialCode;
                      setCountryCode(`+${dialCode}`);
                      // Remove dial code from value to get just the phone number
                      phoneNumber = value.startsWith(dialCode) 
                        ? value.substring(dialCode.length) 
                        : value;
                    } else {
                      // If no dial code, assume it's just the phone number
                      phoneNumber = value;
                    }
                    
                    // Validate phone number length (8-13 digits)
                    const phoneDigits = phoneNumber.replace(/\D/g, '');
                    if (phoneDigits.length <= 13) {
                      setPhone(phoneNumber);
                      // Clear error when valid
                      if (phoneError) {
                        setPhoneError("");
                      }
                    }
                    // If more than 13 digits, don't update (prevent typing)
                  }}
                />
                {phoneError && <h6 className="err_msg">{phoneError}</h6>}
              </div>
            </div>
            <div className="form_btn">
              <Button
                className="btn btn_primary"
                onClick={handleSaveContactSupport}
                disabled={isUpdatingCms}
              >
                {isUpdatingCms ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={1}>
          <EditText content={legal2} setContent={setLegal2} />
          <div className="form_btn">
            <Button
              className="btn btn_primary"
              onClick={handleSavePrivacyPolicy}
              disabled={isUpdatingCms}
            >
              {isUpdatingCms ? "Saving..." : "Save"}
            </Button>
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={2}>
          <EditText content={legal3} setContent={setLegal3} />
          <div className="form_btn">
            <Button
              className="btn btn_primary"
              onClick={handleSaveTermsConditions}
              disabled={isUpdatingCms}
            >
              {isUpdatingCms ? "Saving..." : "Save"}
            </Button>
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={3}>
          <EditText content={legal4} setContent={setLegal4} />
          <div className="form_btn">
            <Button
              className="btn btn_primary"
              onClick={handleSaveAboutUs}
              disabled={isUpdatingCms}
            >
              {isUpdatingCms ? "Saving..." : "Save"}
            </Button>
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={4}>
          <div className="table_header">
            <div className="left_s">
              <SearchBar
                value={searchTerm}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                onCross={() => setSearchTerm("")}
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                placeholder="Search FAQs..."
              />
            </div>
            <div className="right_s">
              <Button
                className="btn btn_primary"
                onClick={handleAddFaq}
              >
                <AddIcon /> Add FAQ
              </Button>
            </div>
          </div>
          <TableContainer className="table_container">
            {isLoadingFaqs ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                <CircularProgress />
              </Box>
            ) : faqs.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                <p>No FAQs found</p>
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow hover>
                    <TableCell>Sr.no</TableCell>
                    <TableCell>Question</TableCell>
                    <TableCell>Answer</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {faqs.map((faq: Faq, index: number) => (
                    <TableRow key={faq._id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{faq.question}</TableCell>
                      <TableCell className="max_c2">
                        <p>{faq.answer}</p>
                      </TableCell>
                      <TableCell>{formatDate(faq.createdAt)}</TableCell>
                      <TableCell>
                        <div className="table_actions v2">
                          <IconButton
                            onClick={() => handleEditFaq(faq)}
                            disabled={isDeleting}
                          >
                            <img src="/static/images/edit2_icon.svg" alt="Edit" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteFaq(faq._id)}
                            disabled={isDeleting}
                          >
                            <img src="/static/images/trash2_icon.svg" alt="Delete" />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </CustomTabPanel>
      
      </div>

      <AddFaq
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        faq={selectedFaq}
        onSuccess={() => {
          refetchFaqs();
        }}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete FAQ"
        message="Are you sure you want to delete this FAQ? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="error"
        isLoading={isDeleting}
        loadingText="Deleting..."
      />
    </>
  );
};

export default ManageCms;
